import { Input } from "src/designsystem/Input/Input";

import { InputRef } from "antd";
import { getLocationFromPlace, getMapApi, parseAddressComponents } from "src/utils/googleMap";
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from "react";
import { useController } from "react-hook-form";
import { FormInputProps } from "./Form.types";
import FormField, { useFormField } from "./FormField/FormField";

export const FormLocationAutoComplete = forwardRef((props: FormInputProps, ref) => {
  const { formFieldProps, childProps } = useFormField(props);
  const { field } = useController({ name: childProps.name });
  const { onChange: onFormFieldChange } = field;
  const [formattedAddress, setFormattedAddress] = useState<string>("");

  const locationSearchInput = useRef<InputRef>(null);

  useImperativeHandle(ref, () => {
    return {
      focus() {
        locationSearchInput?.current?.focus();
      },
    };
  }, []);

  useEffect(() => {
    let autoComplete;

    const initAutoSearch = async () => {
      if (!locationSearchInput?.current) {
        return;
      }
      const mapApi = await getMapApi();
      const { input } = locationSearchInput.current;
      autoComplete = new mapApi.places.Autocomplete(input);
      autoComplete.addListener("place_changed", onPlaceChanged);
    };

    const onPlaceChanged = async () => {
      const place: google.maps.places.PlaceResult = autoComplete.getPlace();

      if (!place.geometry) {
        return;
      }

      const coordinates: { lat: number; lng: number } = getLocationFromPlace(place);
      const address = parseAddressComponents(place.address_components ?? []);

      onFormFieldChange({
        coordinates: {
          latitude: coordinates.lat,
          longitude: coordinates.lng,
        },
        address: {
          ...address,
          formatted: place.formatted_address,
        },
      });
      if (place.formatted_address) {
        setFormattedAddress(place.formatted_address);
      }
    };

    void initAutoSearch();
  }, [onFormFieldChange]);

  useEffect(() => {
    // sets default value for field if it exists
    if (field.value?.address?.formatted && !formattedAddress) {
      setFormattedAddress(field.value.address.formatted);
    }
  }, [field.value, formattedAddress]);

  return (
    <FormField {...formFieldProps}>
      <Input
        {...childProps}
        {...field}
        ref={locationSearchInput}
        value={formattedAddress}
        onChange={(event) => {
          onFormFieldChange({
            value: event.target.value,
          });
          setFormattedAddress(event.target.value);
        }}
      />
    </FormField>
  );
});
